<template>
  <div v-if="!loading">
    <Header :title="name">
      <router-link :to="`/app/${this.$route.params.id}`">
        <Button>View full profile</Button>
      </router-link>
      <Button @click="anonymous = !anonymous" :icon="{ left: `eye-slash` }">
        {{ anonymous ? 'Anonymous Off' : 'Anonymous On' }}
      </Button>
      <Button @click="togglePowerMode" :icon="{ left: `bolt` }">
        {{ powerMode ? 'Power Mode Off' : 'Power Mode On' }}
      </Button>
    </Header>
    <img src="https://cdn.hackthe6ix.com/admin_dashboard/crowdmark_banner.webp" style="max-width: 200px">

    <!-- Background Info -->
    <template v-if="!$route.query.category || $route.query.category === 'portfolio' || !anonymous">
      <div :class="[$style.items, anonymous ? $style.items2 : $style.items3]">
        <PersonalInfo :data="data" v-if="!anonymous" />
        <EducationInfo :data="data" />
        <ExperienceInfo :data="data" />
      </div>
      <div :class="[$style.items]">
        <Grade category="portfolio" :maximumScore="maxScores['portfolio']" :currentState="data.internal.applicationScores.portfolio" :updateScore="updateScore" />
        <hr />
      </div>
    </template>

    <!-- Long Response -->
    <div :class="[$style.items]">
      <template
        v-for="{ name, question, category } in longResponseQuestions"

        v-if="
            category && (!$route.query.category ||
            $route.query.category === category) ||
            !anonymous
          "
      >
        <LongResponse
          :data="data.hackerApplication[name]"
          :title="question"
        />
        <Grade :category="category" v-if="category" :maximumScore="maxScores[category] || 0" :currentState="data.internal.applicationScores[category]" :updateScore="updateScore" />
        <hr />
      </template>
    </div>

    <template>
      <Notes :currentState="data.internal.notes" :updateNotes="updateNotes"/>
      <hr />
    </template>

    <Button @click="getNext">
      Get next candidate
    </Button>
  </div>
</template>

<script>
  import Button from '@hackthe6ix/vue-ui/Button';
  import TextComponent from '@hackthe6ix/vue-ui/Text';
  import swal from 'sweetalert';
  import 'vue-awesome/icons/eye-slash';
  import 'vue-awesome/icons/bolt';
  import Header from '../../../../containers/Header/index';
  import { editObject, getCandidate, getObject, gradeCandidate } from '../../../../utils/api';
  import {applicationQuestionData, maxScores} from '../../../../utils/const';
  import { getUser } from '../../../../utils/UserController';
  import DeleteApplicant from '../actions/DeleteApplicant/index';
  import AdminInfo from '../sections/AdminInfo';
  import AdminRaw from '../sections/AdminRaw';
  import EducationInfo from '../sections/EducationInfo';
  import ExperienceInfo from '../sections/ExperienceInfo';
  import Grade from '../sections/Grade';
  import LocationInfo from '../sections/LocationInfo';
  import LongResponse from '../sections/LongResponse';
  import Notes from '../sections/Notes';
  import PersonalInfo from '../sections/PersonalInfo';

  export default {
  name: `ApplicantDetails`,
  components: {
    DeleteApplicant,
    ExperienceInfo,
    TextComponent,
    EducationInfo,
    PersonalInfo,
    LocationInfo,
    LongResponse,
    AdminInfo,
    AdminRaw,
    Grade,
    Notes,
    Button,
    Header,
  },
  data() {
    return {
      maxScores,
      loading: true,
      data: {},
      adminUser: {},
      longResponseQuestions: applicationQuestionData,
      anonymous: true,
      powerMode: false
    };
  },
  props: {
    id: String,
  },
  computed: {
    name() {
      return this.anonymous
        ? ''
        : `${this.data.firstName} ${this.data.lastName}`;
    },
  },
  watch: {
    powerMode: val => {
      sessionStorage.setItem("powerMode", String(val));
    }
  },
  methods: {
    togglePowerMode() {
      this.powerMode = !this.powerMode;

      if(this.powerMode) {
        swal('Enabled Power Mode', 'Power Mode Controls: 0-9 - Use number keys to assign a score, Enter - go to next applicant, a - toggle anonymous', 'success');
      } else {
        swal('Disabled Power Mode', 'Power Mode has been disabled. Click the button again to turn it back on.', 'error')
      }
    },
    handleKeyDown(e)  {
      if(this.powerMode) {
        if("0" <= e.key && e.key <= "9") {
          const selectedCategory = this.$route.query.category;
          if(selectedCategory) {
            this.updateScore(e.key, selectedCategory);
          }
        }
        else if(e.key === "Enter") {
          this.getNext();
        }
        else if(e.key.toLowerCase() === 'a') {
          this.anonymous = !this.anonymous;
        }
      }
    },
    async updateNotes(text) {
      const result = await editObject('user', {
        _id: this.data._id
      }, {
        internal: {
          notes: text || ''
        }
      });

      if (result.success) {
        this.data.internal.notes = text;

        swal('Success', 'Notes saved successfully!', 'success');
      } else {
        swal('Error', result.data, 'error');
      }
    },
    async updateScore(score, category) {

      const grades = {};
      grades[category] = parseInt(score);

      const result = await gradeCandidate(this.data._id, grades);

      if (result.success) {

        this.data.internal.applicationScores[category].score = score;

        swal('Success', 'Grade recorded successfully!', 'success');
      } else {
        swal('Error', result.data, 'error');
      }
    },
    async getNext() {
      const result = await getCandidate(this.$route.query.category);

      if (result.success) {
        const nextID = result.data._id;

        if (nextID === this.data._id) {
          swal('Uhh this is awkward', 'It looks like by pure luck you randomly drew the same application that\'s being reviewed right now... maybe try again?', 'warning');
        } else {

          // For some reason router push didn't work here
          location.href = `/review/${nextID}${ this.$route.query.category ? '?category=' + this.$route.query.category : '' }`;
        }

      } else {
        if (result.data === 'Error 404: No applications to review') {
          swal('Success', 'No more applications to review! Come back later', 'success').then(() => {
            this.$router.push('/app');
          });
        } else {
          swal('Error', result.data, 'error');
        }
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
    beforeMount() {
    (async () => {
      this.adminUser = await getUser();

      const response = await getObject('user', {
        filter: {
          _id: this.id,
        },
      });

      if (response.success && response.data[0]) {
        this.data = response.data[0];
        this.loading = false;
      } else {
        swal('Error', 'Unable to get user', 'error');
      }
    })();
    this.powerMode = (sessionStorage.getItem("powerMode") ?? "false").toLowerCase() === "true";
  },
};
</script>

<style src="../Applicant.module.scss" lang="scss" module />
